/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Flex, Stack, Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '../item-variant'
import {useProducts, useProduct} from '@salesforce/commerce-sdk-react'
import {getDisplayVariationValues} from '../../utils/product-utils'

/**
 * In the context of a cart product item variant, this component renders a styled
 * list of the selected variation values as well as any promos (w/ info popover).
 */
const ItemAttributes = ({includeQuantity, qty, ...props}) => {
    const variant = useItemVariant()
    const intl = useIntl()

    const variationValues = getDisplayVariationValues(
        variant?.variationAttributes,
        variant?.variationValues
    )

    // get variant info for bundled products in cart page and order history page
    const productBundleIds =
        variant?.bundledProductItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: productBundleVariantData, isLoading: bundleVariantIsLoading} = useProducts(
        {
            parameters: {
                ids: productBundleIds,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && productBundleIds),
            select: (result) => {
                // formats response so we can easily display child quantity/variant selection
                return result?.data?.map((item) => {
                    const quantity = variant?.bundledProductItems.find(
                        (childProduct) => childProduct.productId === item.id
                    )?.quantity
                    return {
                        ...item,
                        quantity,
                        variationValues: getDisplayVariationValues(
                            item?.variationAttributes,
                            item?.variationValues
                        )
                    }
                })
            }
        }
    )

    // get bundle product data for wishlist page
    const {data: productBundleData, isLoading: productBundleIsLoading} = useProduct(
        {
            parameters: {
                id: variant?.id,
                allImages: false
            }
        },
        {
            enabled: Boolean(variant?.type?.bundle && !productBundleIds)
        }
    )

    return (
        <Stack spacing={8} flex={1} {...props}>
            {variationValues &&
                Object.keys(variationValues).map((key) => (
                    <Flex key={`${key}: ${variationValues[key]}`} lineHeight={1}>
                        <Text as="span" color="darkGray" variant="bodyBase2">
                            {intl.formatMessage(
                                {
                                    defaultMessage: '{key}:',
                                    id: 'item_attributes.label.variation_values'
                                },
                                {key: key}
                            )}
                        </Text>
                        &nbsp;
                        <Text as="span" variant="bodyBase2">
                            {variationValues[key]}
                        </Text>
                    </Flex>
                ))}

            {!productBundleIsLoading && productBundleData && !productBundleVariantData && (
                <Box>
                    {productBundleData?.bundledProducts.map(({product, quantity}) => (
                        <Box marginTop={2} key={product.id}>
                            <Text fontSize="sm" color="gray.700" as="b">
                                {product?.name}
                            </Text>
                            <Text fontSize="sm" color="gray.700">
                                {intl.formatMessage({
                                    defaultMessage: 'Qty',
                                    id: 'add_to_cart_modal.label.quantity'
                                })}
                                : {quantity}
                            </Text>
                        </Box>
                    ))}
                </Box>
            )}

            {!bundleVariantIsLoading && productBundleVariantData && (
                <Box>
                    <Text fontSize={15} marginTop={3} fontWeight={500}>
                        {intl.formatMessage({
                            defaultMessage: 'Selected Options',
                            id: 'item_attributes.label.selected_options'
                        })}
                        :
                    </Text>
                    {productBundleVariantData?.map(
                        ({variationValues, name: productName, quantity, id}) => {
                            return (
                                <Box key={id} marginTop={2}>
                                    <Text fontSize="sm" color="gray.700" as="b">
                                        {productName}
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                        {intl.formatMessage({
                                            defaultMessage: 'Qty',
                                            id: 'add_to_cart_modal.label.quantity'
                                        })}
                                        : {quantity}
                                    </Text>
                                    {Object.keys(variationValues).map((key) => {
                                        const selectedVariant = `${key}: ${variationValues[key]}`
                                        return (
                                            <Text
                                                fontSize="sm"
                                                color="gray.700"
                                                key={selectedVariant}
                                            >
                                                {selectedVariant}
                                            </Text>
                                        )
                                    })}
                                </Box>
                            )
                        }
                    )}
                </Box>
            )}

            {variant?.c_orliweb_product_model && (
                <Text as="span" variant="bodyBase2" color="darkGray">
                    {variant?.c_orliweb_product_model}
                </Text>
            )}

            <Flex>
                <Text as="span" color="darkGray" variant="bodyBase2">
                    <FormattedMessage
                        defaultMessage="Item Number"
                        id="item_attributes.label.item_number"
                    />
                    :&nbsp;
                </Text>
                <Text as="span" variant="bodyBase2">
                    {variant?.ean}
                </Text>
            </Flex>

            {includeQuantity && (
                <Flex>
                    <Text as="span" color="darkGray" variant="bodyBase2">
                        <FormattedMessage
                            defaultMessage="Quantity: "
                            id="item_attributes.label.quantity"
                        />
                    </Text>
                    &nbsp;
                    <Text as="span" variant="bodyBase2">
                        {/* Case: custom qty for intial order in exchange order invoice */}
                        {qty || variant.quantity}
                    </Text>
                </Flex>
            )}
        </Stack>
    )
}

ItemAttributes.propTypes = {
    includeQuantity: PropTypes.bool,
    qty: PropTypes.number
}

export default ItemAttributes
